<template>
  <div class="cf-page-container">
    <div
      v-if="isLoadingMessages"
      :class="['cf-container-full-width', { 'cf-page-loader': isLoadingMessages }]"
    >
      <div class="cf-loader" />
    </div>

    <template v-else>
      <div
        v-if="messages.length === 0 && !isLoadingMessages"
        class="cf-card cf-card--mobile cf-flex cf-flex--column cf-flex--gap-20"
      >
        <h1 class="cf-title cf-title--1">
          {{ inboxTitle }}
        </h1>

        <div class="cf-card cf-card--mobile cf-flex cf-flex--justify-space-between cf-flex--gap-12 extra-padding-full">
          <div class="cf-flex cf-flex--column cf-flex--gap-8">
            <h2 class="cf-title cf-title--3 cf-title--light-gray">
              {{ emptyStateContent.title }}
            </h2>

            <p class="cf-subtitle cf-subtitle--2 cf-subtitle--light-gray cf-text-center">
              {{ emptyStateContent.description }}
            </p>
          </div>
          <span class="icon icon--large far fa-envelope light-gray" />
        </div>
      </div>

      <div
        v-else
        class="cf-card cf-card--mobile cf-flex cf-flex--column cf-flex--gap-20"
      >
        <h1 class="cf-title cf-title--1">
          {{ inboxTitle }}
        </h1>

        <div
          v-for="message in messages"
          :key="message.publicId"
          class="cf-card cf-card--mobile cf-grid cf-grid--inbox-message cf-cursor-pointer"
          :class="{
            'cf-card--unread': message.isUnread,
            'cf-card--read': !message.isUnread
          }"
          @click="openMessage(message)"
        >
          <!-- Date/Time Column -->
          <div>
            <span
              class="cf-subtitle cf-subtitle--3"
              :class="{ 'cf-subtitle--bold': message.isUnread }"
            >
              {{ formatMessageDate(message.deliveredDate) }}
            </span>
          </div>

          <!-- Message Content Column -->
          <div class="cf-flex cf-flex--column cf-flex--gap-8">
            <p
              class="cf-title cf-title--3"
              :class="{ 'regular': !message.isUnread }"
            >
              {{ message.inboxMessage.title }}
            </p>

            <p class="cf-subtitle cf-subtitle--2 cf-subtitle--truncate-2-lines">
              {{ message.inboxMessage.bodyContent }}
            </p>
          </div>

          <!-- Delete Button Column -->
          <div>
            <button
              type="button"
              class="cf-button cf-button--tertiary cf-button--fixed-24"
              title="Delete message"
              @click.stop="deleteMessage(message.publicId)"
            >
              <span class="cf-icon cf-icon--sm far fa-trash-alt" />
            </button>
          </div>
        </div>

        <!-- Invisible trigger for infinite scroll -->
        <div
          v-if="hasMoreMessages && !isLoadingMoreMessages"
          ref="loadMoreTrigger"
          class="cf-infinite-scroll-trigger"
        />

        <div
          v-if="isLoadingMoreMessages"
          class="cf-flex cf-flex--column cf-flex--align-center"
        >
          <div class="cf-loader cf-loader--sm" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import moment from 'moment-timezone';

  import calculateDeviceTimezone from '@/helpers/calculate-device-timezone';
  import deviceUses24Hour from '@/helpers/device-uses-24-hour';
  import formatDate from '@/helpers/format/format-date';

  import errorHandlers from '@/services/error-handlers';
  import events from '@/services/events';

  export default {
    name: 'OrderInboxPage',

    metaInfo() {
      return {
        title: this.inboxTitle || 'Inbox'
      };
    },

    data() {
      return {
        messages: [],
        isLoadingMessages: true,
        isLoadingMoreMessages: false,
        currentPage: 0,
        pageSize: 10,
        hasMoreMessages: false,
        totalPages: 0
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('session', ['currentUser']),

      inboxTitle() {
        return this.content.MenuDropdown?.metadata?.itemInbox || { title: 'Inbox' };
      },

      emptyStateContent() {
        return this.content.MessageInbox || {
          title: 'No messages',
          description: 'Check back soon!'
        };
      }
    },

    async created() {
      await this.loadMessages();
      this.setupIntersectionObserver();
    },

    beforeUnmount() {
      if (this.observer) {
        this.observer.disconnect();
      }
    },

    methods: {
      async loadMessages(page = 0) {
        try {
          if (page === 0) {
            this.isLoadingMessages = true;
          }
          else {
            this.isLoadingMoreMessages = true;
          }

          const { data, paging } = await this.$store.dispatch('session/loadInboxMessages', {
            currentPage: page,
            pageSize: this.pageSize
          });

          if (page === 0) {
            this.messages = data;
          }
          else {
            this.messages = [...this.messages, ...data];
          }

          this.currentPage = page;
          this.totalPages = paging.totalPages;
          this.hasMoreMessages = page < paging.totalPages - 1;
        }
        catch (error) {
          errorHandlers.generic(error);
        }
        finally {
          this.isLoadingMessages = false;
          this.isLoadingMoreMessages = false;
        }
      },

      async loadMoreMessages() {
        if (this.hasMoreMessages && !this.isLoadingMoreMessages) {
          await this.loadMessages(this.currentPage + 1);
        }
      },

      setupIntersectionObserver() {
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && this.hasMoreMessages && !this.isLoadingMoreMessages) {
                this.loadMoreMessages();
              }
            });
          });

          this.$nextTick(() => {
            if (this.$refs.loadMoreTrigger) {
              this.observer.observe(this.$refs.loadMoreTrigger);
            }
          });
        }
      },

      async openMessage(message) {
        if (message.isUnread) {
          await this.markMessageAsRead(message.publicId);
        }

        this.$openModal('inboxMessage', {
          messageId: message.publicId,
          message,
          onDelete: () => this.deleteMessage(message.publicId)
        });
      },

      async markMessageAsRead(messageId) {
        try {
          await this.$store.dispatch('session/markInboxMessageAsRead', messageId);

          const messageIndex = this.messages.findIndex(msg => msg.publicId === messageId);
          if (messageIndex !== -1) {
            this.messages[messageIndex].isUnread = false;
          }
        }
        catch (error) {
          errorHandlers.generic(error);
        }
      },

      async deleteMessage(messageId) {
        try {
          const messageToDelete = this.messages.find(msg => msg.publicId === messageId);

          await this.$store.dispatch('session/deleteInboxMessage', messageId);

          this.messages = this.messages.filter(msg => msg.publicId !== messageId);

          events.emit('notification', {
            type: 'success',
            message: 'Message deleted successfully'
          });

          if (messageToDelete && messageToDelete.isUnread) {
            await this.$store.dispatch('session/decrementUnreadInboxMessageCount');
          }
        }
        catch (error) {
          errorHandlers.generic(error);
        }
      },

      formatMessageDate(deliveredDate) {
        if (!deliveredDate) return '';

        const timezone = calculateDeviceTimezone();
        const messageDate = moment(deliveredDate).tz(timezone);
        const now = moment().tz(timezone);

        // If today, show time
        if (messageDate.isSame(now, 'day')) {
          return messageDate.format(deviceUses24Hour() ? 'HH:mm' : 'h:mm A');
        }

        // If this year, show "Feb 25" (en-US) / "25 Feb" (en-GB) format
        if (messageDate.isSame(now, 'year')) {
          return formatDate(messageDate.toDate(), { style: 'monthDay', timeZone: timezone });
        }

        return formatDate(messageDate.toDate(), { timeZone: timezone });
      }
    }
  };
</script>

<style lang="scss" scoped>
  .cf-card--unread {
    background-color: #ffffff;
  }

  .cf-card--read {
    background-color: var(--color-light-gray);
  }

  .cf-subtitle--truncate-2-lines {
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .cf-infinite-scroll-trigger {
    height: 1px;
    width: 100%;
    opacity: 0;
    pointer-events: none;
  }
</style>
